import { useCallback, useRef, useState } from "react";
import {
  DOCUMENT_SIDES,
  ID_DOCUMENT_VERIFICATION_STATUSES,
} from "../../../config";
import { useActionCallback } from "../../../../../../utils/hooks/useActionCallback";
import { useIdDocumentVerificationVerifyAdvance } from "./useIdDocumentVerificationVerifyAdvance";

export const useDocumentIdPictureUploadFlow = ({
  dispatch,
  onSubmitForm,
  onboardingDetailsId,
  idDocumentVerification,
  frontBase64,
  backBase64,
  frontRef,
  backRef,
}) => {
  const [uploadingFront, setUploadingFront] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);
  const [failedSide, setFailedSide] = useState(null);

  const backBase64Ref = useRef(backBase64);
  backBase64Ref.current = backBase64;

  const onVerifyDone = useCallback(
    (verificationStatus) => {
      if (verificationStatus === ID_DOCUMENT_VERIFICATION_STATUSES.COMPLETED) {
        onSubmitForm({});
        return;
      }
      if (verificationStatus === ID_DOCUMENT_VERIFICATION_STATUSES.FAILED) {
        onSubmitForm({});
      }
    },
    [onSubmitForm]
  );

  const { isVerifying, requestVerify } = useIdDocumentVerificationVerifyAdvance(
    {
      dispatch,
      onboardingDetailsId,
      idDocumentVerification,
      onVerifyDone,
    }
  );

  const onFrontFulfilled = useCallback(() => {
    setUploadingFront(false);
    setUploadingBack(true);
    dispatch({
      type: "REQUEST_PUT_ID_DOCUMENT_VERIFICATION_BACK",
      payload: {
        onboarding_details_id: onboardingDetailsId,
        photo: backBase64Ref.current,
      },
    });
  }, [dispatch, onboardingDetailsId]);

  const onFrontRejected = useCallback(() => {
    setUploadingFront(false);
    setFailedSide(DOCUMENT_SIDES.FRONT);
  }, []);

  const onBackFulfilled = useCallback(() => {
    setUploadingBack(false);
    requestVerify();
  }, [requestVerify]);

  const onBackRejected = useCallback(() => {
    setUploadingBack(false);
    setFailedSide(DOCUMENT_SIDES.BACK);
  }, []);

  useActionCallback(
    "REQUEST_PUT_ID_DOCUMENT_VERIFICATION_FRONT_FULFILLED",
    onFrontFulfilled
  );
  useActionCallback(
    "REQUEST_PUT_ID_DOCUMENT_VERIFICATION_FRONT_REJECTED",
    onFrontRejected
  );
  useActionCallback(
    "REQUEST_PUT_ID_DOCUMENT_VERIFICATION_BACK_FULFILLED",
    onBackFulfilled
  );
  useActionCallback(
    "REQUEST_PUT_ID_DOCUMENT_VERIFICATION_BACK_REJECTED",
    onBackRejected
  );

  const handleContinue = useCallback(() => {
    setUploadingFront(true);
    dispatch({ type: "CLEAR_ID_DOCUMENT_VERIFICATION_ERROR" });
    dispatch({
      type: "REQUEST_PUT_ID_DOCUMENT_VERIFICATION_FRONT",
      payload: {
        onboarding_details_id: onboardingDetailsId,
        photo: frontBase64,
      },
    });
  }, [dispatch, onboardingDetailsId, frontBase64]);

  const handleRetry = useCallback(() => {
    setUploadingFront(false);
    setUploadingBack(false);
    setFailedSide(null);
    dispatch({ type: "CLEAR_ID_DOCUMENT_VERIFICATION_ERROR" });
    frontRef.current?.reset?.();
    backRef.current?.reset?.();
    dispatch({
      type: "REQUEST_POST_ID_DOCUMENT_VERIFICATION_START",
      payload: {
        onboarding_details_id: onboardingDetailsId,
        document_type: idDocumentVerification?.documentType,
      },
    });
  }, [
    dispatch,
    frontRef,
    backRef,
    onboardingDetailsId,
    idDocumentVerification?.documentType,
  ]);

  return {
    uploadingFront,
    uploadingBack,
    isVerifying,
    failedSide,
    handleContinue,
    handleRetry,
  };
};
